import React, {
    ReactElement,
} from "react";
import { IconType } from "react-icons";
import { AiOutlineMail, AiOutlineMessage } from "react-icons/ai";
import { BiHash, BiRocket, BiTime } from "react-icons/bi";
import {
    BsAlarm, BsHandThumbsUp, BsShopWindow, BsShuffle,
} from "react-icons/bs";
import { FaWpforms } from "react-icons/fa";
import { FiColumns, FiExternalLink } from "react-icons/fi";
import { HiOutlineTemplate } from "react-icons/hi";
import { IoPeople, IoPerson } from "react-icons/io5";
import { MdEditNote } from "react-icons/md";
import { SiCodemagic, SiSlack } from "react-icons/si";
import { VscSettingsGear } from "react-icons/vsc";
import { Loading } from "../../components";
import useRouting, { useParams } from "../../contexts/Routing";
import { Settings, useGetSettingsQuery } from "../../__generated__/api";
import { ErrorRemote } from "../error/Error";
import Styles from "./Administrator.module.scss";
import SlackSettings from "./sections/channels/slack/slack";
import ChannelTagSettings from "./sections/channels/channel-tags/ChannelTagSettings";

import SLASettings from "./sections/sla/SLA";

import Customization from "./sections/customization/Customization";
import Feedback from "./sections/feedback/Feedback";
import PortalSettings from "./sections/portal/Portal";
import ActionForm from "./sections/request-form/ActionForm";
import RequestTypes from "./sections/request-types/RequestTypes";
import TicketStates from "./sections/ticket-states/TicketStates";

import EmailSettings from "./sections/channels/email/Email";
import WebChatSettings from "./sections/channels/web-chat/WebChat";
import CustomField from "./sections/custom-field/CustomField";
import QueueSettings from "./sections/queues/Queues";
import RequestForm from "./sections/request-form/RequestForm";

import ChatSettings from "./sections/chat/Chat";
import TimeSettings from "./sections/time/Time";
import UserGroupSettings from "./sections/user-groups/UserGroups";
import UsersSettings from "./sections/users/Users";
import WorkflowServicesSettings from "./sections/workflow-services/Services";
import WorkflowViewServiceSettings from "./sections/workflow-services/ViewService";

import ChannelInvitesSettings from "./sections/channel-invites/ChannelInvites";

import ArtificialIntelligence from "./sections/artificial-intelligence/ArtificialIntelligence";
import { SettingsProps } from "./sections/base";
import { FOQAL_GRAPHQL_URL } from "../../api";

interface Section {
    title?: string;
    name?: string;
    slug: string;
    page?: (props: SettingsProps) => ReactElement;
    url?: string;
    icon?: IconType;
}
interface Category {
    name: string;
    sections: Section[]
}

// const FOQAL_GRAPHQL_URL = process.env.REACT_APP_API_URL;

const SECTIONS: Category[] = [
    {
        name: "Support",
        sections: [
            {
                title: "Settings",
                name: "Customize",
                slug: "customize",
                page: Customization,
                icon: MdEditNote,
            },
            {
                title: "Time Settings",
                name: "Time",
                slug: "time",
                page: TimeSettings,
                icon: BiTime,
            },

            {
                title: "Feedback",
                name: "Feedback",
                slug: "feedback",
                page: Feedback,
                icon: BsHandThumbsUp,
            },
            {
                title: "Request Types",
                name: "Request Types",
                slug: "request-types",
                page: RequestTypes,
                icon: FaWpforms,
            },
            {
                title: "Custom Field",
                slug: "request-fields",
                page: CustomField,
            },
            {
                title: "Request Type",
                slug: "request-forms",
                page: RequestForm,

            },
            {
                name: "Queues",
                title: "Queues",
                slug: "queues",
                page: QueueSettings,
                icon: BsShuffle,
            },
            {
                name: "SLA",
                title: "SLA",
                slug: "sla",
                page: SLASettings,
                icon: BsAlarm,
            },
            {
                name: "Columns",
                title: "Columns",
                slug: "ticket-states",
                page: TicketStates,
                icon: FiColumns,

            },
            {
                name: "AI",
                title: "Artificial Intelligence",
                slug: "ai",
                page: ArtificialIntelligence,
                icon: SiCodemagic,
            },

        ],
    },
    {
        name: "Channels",
        sections: [

            {
                title: "Channel Tag Settings",
                name: "Tags",
                slug: "channel-tags",
                page: ChannelTagSettings,
                icon: BiHash,
            },
            {
                title: "Slack Settings",
                name: "Slack",
                slug: "channels-slack",
                page: SlackSettings,
                icon: SiSlack,
            },
            {
                title: "Chat",
                slug: "chat",
                page: ChatSettings,
            },

            {
                title: "Email Settings",
                name: "Email",
                slug: "channels-emails",
                page: EmailSettings,
                icon: AiOutlineMail,
            },
            {
                title: "Web Chat",
                name: "Web Chat",
                slug: "web-chat",
                page: WebChatSettings,
                icon: AiOutlineMessage,

            },

        ],
    },
    {
        name: "Users",
        sections: [
            {
                name: "Users",
                title: "Users",
                slug: "users",
                page: UsersSettings,
                icon: IoPerson,
            },
            {
                name: "Groups",
                title: "Groups",
                slug: "user-groups",
                page: UserGroupSettings,
                icon: IoPeople,
            },
        ],
    },
    {
        name: "Automation",
        sections: [
            {
                name: "Services",
                title: "Services",
                slug: "workflow-services",
                page: WorkflowServicesSettings,
                icon: BsShopWindow,
            },
            {
                title: "",
                slug: "workflow-service",
                page: WorkflowViewServiceSettings,
            },
            {
                name: "Actions",
                title: "Actions",
                slug: "workflow-actions",
                page: ActionForm,
                icon: BiRocket,
            },

        ],
    },
    {
        name: "Portal",
        sections: [
            {
                title: "Portal Settings",
                name: "Settings",
                slug: "portal",
                page: PortalSettings,
                icon: VscSettingsGear,
            },
            {
                title: "Channel Templates",
                name: "Channel Templates",
                slug: "channel-invites",
                page: ChannelInvitesSettings,
                icon: HiOutlineTemplate,
            },

        ],
    },

    {
        name: "Billing",
        sections: [
            {
                name: "Billing",
                slug: "billing-portal",
                url: new URL("/api/stripe/portal", FOQAL_GRAPHQL_URL).toString(),
            },

        ],
    },

];

const DEFAULT = SECTIONS[0].sections[0];
const FLAT_SECTIONS = SECTIONS.flatMap(row => row.sections);

export default function Administrator(): ReactElement {
    const { Paths, historyPush } = useRouting();
    const {
        data: settings, loading: isLoading, error, refetch,
    } = useGetSettingsQuery();
    let { setting } = useParams(Paths.ADMINISTRATOR_SETTING);
    const adminIdSettings = useParams(Paths.ADMINISTRATOR_SETTING_ID);

    if (!setting) {
        setting = adminIdSettings.setting;
        if (!setting) {
            const defaultItem = FLAT_SECTIONS.find(row => row === DEFAULT);
            if (defaultItem) {
                historyPush(Paths.ADMINISTRATOR_SETTING, { setting: defaultItem.slug });
            }
        }
    }

    const container = setting ? FLAT_SECTIONS.find(page => page.slug === setting) : undefined;
    const Page = container ? container.page : undefined;

    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }
    return (

        <div className="d-flex justify-content-between align-items-stretch h-100">
            <div className={`overflow-hidden flex-shrink-0 mb-5 ${Styles.sidebar}`}>
                <div className="position-sticky">
                    <div className="text-center pt-3 pb-2">
                        <h4 className="text-gray-500">Settings</h4>
                    </div>
                    {SECTIONS.map(group => (
                        <div key={group.name}>
                            <h5 className="mt-3 ms-2">
                                {group.name}
                            </h5>
                            <div className="" role="tablist">
                                {group.sections.filter(section => section.name).map(section => {
                                    const Icon = section.icon;
                                    const isSelected = container?.slug === section.slug;
                                    return (
                                        (section.url ? (
                                            <a
                                                key={section.slug}
                                                className={`mt-2 border-start d-flex text-decoration-none `
                                                    + ` list-border`
                                                    + ` rounded-0  align-items-center my-2 py-0 text-default`}
                                                href={section.url}
                                                rel="noreferrer"
                                                target="_blank"
                                            >
                                                <FiExternalLink className="text-light me-2" />
                                                {section.name}
                                            </a>
                                        ) : (
                                            <button
                                                key={section.slug}
                                                type="button"
                                                className={`list-group-item list-group-item-action border-start d-flex `
                                                    + ` list-border ${isSelected
                                                        ? "fw-semibold list-border-primary " : ""}`
                                                    + ` rounded-0  align-items-center mb-1`}
                                                onClick={() => {
                                                    refetch();
                                                    historyPush(Paths.ADMINISTRATOR_SETTING, { setting: section.slug });
                                                }}
                                            >
                                                {Icon && <Icon className={`me-2 ${!isSelected ? "text-light" : ""}`} />}
                                                {section.name}
                                            </button>
                                        ))

                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

            </div>

            <div className="flex-grow-1 p-3 d-flex flex-column shadow">
                {container?.title && (
                    <h5 className="mb-4">
                        {container?.title}
                    </h5>
                )}

                {Page ? <Page settings={settings?.settings as Settings} /> : undefined}

            </div>

        </div>

    );
}
